import { useCallback, useEffect } from "react";
import { useLocation, useNavigationType, useSearchParams } from "react-router";

/**
 * Writing URL search params without clobbering a write that has not committed yet (POS2-1048).
 *
 * `setSearchParams`' own functional updater is handed the params captured at the render its
 * setter was created in, not the live URL (react-router 7.13.1: `searchParams` is memoised on
 * `[location.search]` and the setter closes over it). Two writes inside one commit window
 * therefore both build from the same snapshot, and the later one silently drops the earlier
 * one's param — switch Explore to Weekly, then quickly to kg, and the granularity reverts.
 *
 * Worse than a lost toggle: `useWasteFilters` seeds `site_id` from an effect gated on the sites
 * query, so it lands whenever that request resolves — including mid-click. A click that dropped
 * that write left the URL with no `site_id` for the rest of the visit, because the hook latches
 * `seeded` and never retries. Explore and the dashboard still render the right data (both fall
 * back to their own default site), but the scope stops *travelling*: the shared filter store
 * mirrors a null site, so a hop to Transactions — which has no default — lands org-wide, and a
 * copied link no longer carries the site it was read at. Measured at 8 in 12 attempts before
 * this fix.
 *
 * So the base for the next write is what we last wrote, not what the last render saw.
 *
 * `location` cannot be trusted blindly either: it lags writes that are still in flight, so
 * adopting it unconditionally overwrites a newer tracked value with an older one. Hence
 * `written`: a location we have written is our own echo and changes nothing, and only a location
 * we never wrote is external (Back, a Link, a redirect) and becomes the new truth.
 *
 * That check must be a **membership test, not a queue**. Several hooks run this effect for the
 * same location — Explore alone mounts two `useWasteFilters` (the page and the FilterPopover) —
 * so anything that consumes an entry drains it for the first hook and leaves the rest treating
 * their own echo as external, adopting an older URL. That cost ~3 lost `site_id`s in 30 runs.
 *
 * State is module-level because there is one URL, so there is one latest value. A per-hook ref
 * would still let two different hooks clobber each other, which is the dashboard's exposure —
 * `useLens` and `useWasteFilters` are both mounted there.
 *
 * Reading `window.location.search` instead would need no tracking, but it is wrong under
 * `MemoryRouter` — which every hook test uses — because memory history never touches it.
 */
let latestSearch: string | null = null;
/** Everything written since the last external navigation, so an echo is recognisable from any
 * hook instance. Never pruned on the way through: navigations can arrive later than the write
 * that caused them, and forgetting one makes it look external — which then adopts a pre-seed URL
 * and drops `site_id` all over again. A POP is what clears this (see below). */
let written = new Set<string>();

/** Compare and store search strings one way, since `location.search` carries a leading "?". */
function normalise(search: string): string {
  return search.startsWith("?") ? search.slice(1) : search;
}

/**
 * @param replace whether writes replace the history entry. Decided once per consumer rather than
 * per call: Kitchen Display deliberately pushes so Back closes the overlay, everything else
 * replaces so filter fiddling doesn't fill the history stack.
 */
export function useSearchParamWriter({ replace }: { replace: boolean }) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [, setSearchParams] = useSearchParams();

  useEffect(() => {
    const current = normalise(location.search);
    // Back/Forward is unambiguously the user moving through history, so it is the truth even
    // when it lands on a URL we wrote earlier — which `written` would otherwise dismiss as an
    // echo, leaving the next write to resurrect what was just navigated away from.
    if (navigationType === "POP") {
      latestSearch = current;
      written = new Set();
      return;
    }
    if (current === latestSearch) return;
    // A stale echo of one of our own writes — the tracked value is already newer than this.
    if (written.has(current)) return;
    // Nothing we wrote: a navigation from outside, which outranks anything we remember.
    latestSearch = current;
    written = new Set();
  }, [location.search, navigationType]);

  const write = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const next = new URLSearchParams(latestSearch ?? "");
      mutate(next);
      latestSearch = next.toString();
      written.add(latestSearch);
      setSearchParams(next, { replace });
    },
    [setSearchParams, replace],
  );

  /** Drop every param. Separate from `write` because it must also clear the tracked value —
   * otherwise the next write rebuilds from the params that were just wiped and resurrects them. */
  const reset = useCallback(() => {
    latestSearch = "";
    written.add("");
    setSearchParams(new URLSearchParams(), { replace });
  }, [setSearchParams, replace]);

  return { write, reset };
}
